import { Link } from "@tanstack/react-router";
import { Badge, Button, Icon, Switch } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";

import { CollapsibleSection } from "./CollapsibleSection";
import { SECTION_ID } from "../lib/admin-vendor-form.utils";

export interface LinkedPartnerOrg {
  id: string;
  name: string;
}

interface PartnerOrgSectionProps {
  isEdit: boolean;
  createPartnerOrg: boolean;
  onCreatePartnerOrgChange: (value: boolean) => void;
  linkedPartner: LinkedPartnerOrg | null;
  connecting: boolean;
  unlinking: boolean;
  canConnect: boolean;
  canUnlink: boolean;
  onConnect: () => void;
  onUnlink: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Super/ops Partner org step: create-on-save, connect a legacy vendor, or
 * inspect/unlink the org staff actually sign in with.
 */
export function PartnerOrgSection({
  isEdit,
  createPartnerOrg,
  onCreatePartnerOrgChange,
  linkedPartner,
  connecting,
  unlinking,
  canConnect,
  canUnlink,
  onConnect,
  onUnlink,
  open,
  onOpenChange,
}: PartnerOrgSectionProps) {
  const { t } = useTranslation();

  const hint = !isEdit
    ? t(
        "marketplace.admin.sections.partnerHint",
        "Create or unlink the Partner organization this partner logs in with. Invites stay in Identity Hub.",
      )
    : linkedPartner
      ? t(
          "marketplace.admin.partner.linkedHint",
          "Partner staff log in here. Invite them in Identity Hub.",
        )
      : t(
          "marketplace.admin.partner.noneSectionHint",
          "Connect so this partner can log in. Invites stay in Identity Hub.",
        );

  return (
    <CollapsibleSection
      id={SECTION_ID.partner}
      step={2}
      title={t("marketplace.admin.partner.section", "Partner org")}
      hint={hint}
      open={open}
      onOpenChange={onOpenChange}
    >
      {!isEdit ? (
        <CreatePartnerOrgToggle
          checked={createPartnerOrg}
          onCheckedChange={onCreatePartnerOrgChange}
        />
      ) : linkedPartner ? (
        <LinkedPartnerOrgPanel
          name={linkedPartner.name}
          unlinking={unlinking}
          canUnlink={canUnlink}
          onUnlink={onUnlink}
        />
      ) : (
        <UnlinkedPartnerOrgPanel
          connecting={connecting}
          canConnect={canConnect}
          onConnect={onConnect}
        />
      )}
    </CollapsibleSection>
  );
}

function CreatePartnerOrgToggle({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  const label = t(
    "marketplace.admin.partner.createLabel",
    "Create Partner org on save",
  );

  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2.5">
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs leading-5 text-muted-foreground">
          {t(
            "marketplace.admin.partner.createHint",
            "Creates a Partner organization named after this partner. Staff are invited later in Identity Hub — this does not send a login email.",
          )}
        </span>
      </span>
      <Switch
        id="vf-create-partner-org"
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={label}
      />
    </label>
  );
}

function LinkedPartnerOrgPanel({
  name,
  unlinking,
  canUnlink,
  onUnlink,
}: {
  name: string;
  unlinking: boolean;
  canUnlink: boolean;
  onUnlink: () => void;
}) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const canOpenIdentityHub = useIamPermission(perms.IDENTITY_HUB_VIEW_PAGE);

  const facts = [
    {
      label: t("marketplace.admin.partner.loginLabel", "Staff login"),
      value: t("marketplace.admin.partner.loginValue", "Production orders"),
    },
    {
      label: t("marketplace.admin.partner.invitesLabel", "Invites"),
      value: t(
        "marketplace.admin.partner.invitesValue",
        "Identity Hub. This screen does not send a login email.",
      ),
    },
    {
      label: t("marketplace.admin.partner.logoLabel", "Logo"),
      value: t(
        "marketplace.admin.partner.logoValue",
        "Follows the partner logo when you save.",
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <Badge variant="status-connected">
            {t("marketplace.admin.partner.linkedStatus", "Linked")}
          </Badge>
          <h3 className="text-sm font-semibold tracking-tight text-foreground">{name}</h3>
        </div>
        {canUnlink ? (
          <Button
            type="button"
            variant="ghost"
            disabled={unlinking}
            className="cursor-pointer"
            onClick={onUnlink}
          >
            {unlinking
              ? t("marketplace.admin.partner.unlinking", "Unlinking…")
              : t("marketplace.admin.partner.unlink", "Unlink")}
          </Button>
        ) : null}
      </div>

      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
        {facts.map((fact) => (
          <div key={fact.label} className="min-w-0">
            <dt className="font-mono text-[10px] font-medium uppercase tracking-label text-muted-foreground">
              {fact.label}
            </dt>
            <dd className="text-data mt-1 text-foreground">{fact.value}</dd>
          </div>
        ))}
      </dl>

      {canOpenIdentityHub ? (
        <Button asChild variant="outline" className="w-fit cursor-pointer">
          <Link to="/iam">
            <Icon name="arrow-up-right" color="inherit" />
            {t("marketplace.admin.partner.inviteCta", "Open Identity Hub")}
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

function UnlinkedPartnerOrgPanel({
  connecting,
  canConnect,
  onConnect,
}: {
  connecting: boolean;
  canConnect: boolean;
  onConnect: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <Badge variant="outline">
            {t("marketplace.admin.partner.notLinkedStatus", "Not linked")}
          </Badge>
          <h3 className="text-sm font-semibold tracking-tight text-foreground">
            {t("marketplace.admin.partner.none", "No Partner org linked")}
          </h3>
        </div>
        {canConnect ? (
          <Button
            type="button"
            variant="outline"
            disabled={connecting}
            className="cursor-pointer"
            onClick={onConnect}
          >
            {connecting
              ? t("marketplace.admin.partner.connecting", "Connecting…")
              : t("marketplace.admin.partner.connect", "Connect Partner org")}
          </Button>
        ) : null}
      </div>

      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="font-mono text-[10px] font-medium uppercase tracking-label text-muted-foreground">
            {t("marketplace.admin.partner.connectDoesLabel", "Connect does")}
          </dt>
          <dd className="text-data mt-1 text-foreground">
            {t(
              "marketplace.admin.partner.connectDoesValue",
              "Creates a Partner organization named after this partner and copies the partner logo.",
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="font-mono text-[10px] font-medium uppercase tracking-label text-muted-foreground">
            {t("marketplace.admin.partner.invitesLabel", "Invites")}
          </dt>
          <dd className="text-data mt-1 text-foreground">
            {t(
              "marketplace.admin.partner.invitesValue",
              "Identity Hub. This screen does not send a login email.",
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}
